import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Flame, LogOut, ChevronLeft, AlertTriangle, RefreshCw, CheckCircle2, Pencil, Users, Target, BarChart3, Download } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from "recharts";
import api, { formatError } from "../lib/api";
import { formatFrDate } from "../lib/date";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "./ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

const TYPE_LABEL = { running: "Course", strength: "Force", hyrox_wod: "WOD HYROX" };
const ISSUE_LABEL = { injury: "Blessure", fatigue: "Fatigue / surmenage", unavailable: "Indisponibilité" };

function paceToSec(p) {
  if (!p || typeof p !== "string" || !p.includes(":")) return null;
  const [m, s] = p.split(":").map(Number);
  return isNaN(m) || isNaN(s) ? null : m * 60 + s;
}

function MiniChart({ title, data, color, invert }) {
  if (!data.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <h4 className="text-sm uppercase tracking-widest text-muted-foreground mb-3">{title}</h4>
      <ResponsiveContainer width="100%" height={160}>
        <LineChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
          <CartesianGrid stroke="#27272A" strokeOpacity={0.4} vertical={false} />
          <XAxis dataKey="label" tick={{ fill: "#71717A", fontSize: 11 }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fill: "#71717A", fontSize: 11 }} axisLine={false} tickLine={false} reversed={invert} domain={["auto", "auto"]} />
          <Tooltip contentStyle={{ background: "#141414", border: "1px solid #27272A", borderRadius: 8, color: "#fff" }} />
          <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2.5} dot={{ r: 3, fill: color }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function EditSessionDialog({ open, onClose, session, onSave }) {
  const [d, setD] = useState({});
  useEffect(() => { if (session) setD({ ...session, mainText: (session.main || []).join("\n") }); }, [session]);
  if (!session) return null;
  const save = () => {
    const updated = { ...session, title: d.title, warmup: d.warmup, finisher: d.finisher, recovery: d.recovery, main: (d.mainText || "").split("\n").filter(Boolean) };
    onSave(updated);
    onClose();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border w-[calc(100%-1.5rem)] max-w-lg max-h-[90vh] overflow-y-auto p-5 sm:p-6">
        <DialogHeader><DialogTitle className="font-display text-xl">Éditer la séance</DialogTitle>
          <DialogDescription className="text-muted-foreground">Ajuste le contenu avant de valider le programme.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Titre</Label><Input data-testid="edit-title" value={d.title || ""} onChange={(e) => setD({ ...d, title: e.target.value })} className="h-11 bg-secondary border-border mt-1" /></div>
          <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Échauffement</Label><Textarea data-testid="edit-warmup" value={d.warmup || ""} onChange={(e) => setD({ ...d, warmup: e.target.value })} className="bg-secondary border-border mt-1" /></div>
          <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Corps de séance (1 par ligne)</Label><Textarea data-testid="edit-main" value={d.mainText || ""} onChange={(e) => setD({ ...d, mainText: e.target.value })} className="bg-secondary border-border mt-1 min-h-[100px]" /></div>
          <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Finisher</Label><Input data-testid="edit-finisher" value={d.finisher || ""} onChange={(e) => setD({ ...d, finisher: e.target.value })} className="h-11 bg-secondary border-border mt-1" /></div>
          <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Récupération</Label><Input data-testid="edit-recovery" value={d.recovery || ""} onChange={(e) => setD({ ...d, recovery: e.target.value })} className="h-11 bg-secondary border-border mt-1" /></div>
          <Button data-testid="edit-save-btn" onClick={save} className="w-full h-12 bg-primary hover:bg-primary/90 text-white rounded-md font-semibold">Enregistrer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function CoachHome() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [athletes, setAthletes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [program, setProgram] = useState(null);
  const [profile, setProfile] = useState({});
  const [logs, setLogs] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [editSession, setEditSession] = useState(null);
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenText, setRegenText] = useState("");
  const [regenSession, setRegenSession] = useState(null);
  const [regenSessText, setRegenSessText] = useState("");
  const [regenSessLoading, setRegenSessLoading] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [stats, setStats] = useState(null);
  const [reviews, setReviews] = useState([]);

  const loadStats = async () => {
    setShowStats(true);
    try {
      const { data } = await api.get("/coach/analytics");
      setStats(data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || err.message); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/coach/analytics/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "roxlab_analytics.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || err.message); }
  };

  const loadAthletes = useCallback(async () => {
    const { data } = await api.get("/coach/athletes");
    setAthletes(data || []);
  }, []);

  useEffect(() => { loadAthletes(); }, [loadAthletes]);

  const openAthlete = async (a) => {
    setSelected(a);
    setProgram(null);
    setProfile({});
    setLogs([]);
    setReviews([]);
    const [prog, prof, lg, rev] = await Promise.allSettled([
      a.program_id ? api.get(`/programs/${a.program_id}`) : Promise.resolve({ data: null }),
      api.get(`/profile/${a.id}`),
      api.get(`/logs/${a.id}`),
      api.get(`/coach/weekly-reviews/${a.id}`),
    ]);
    const pdata = prog.status === "fulfilled" ? prog.value.data : null;
    setProgram(pdata && pdata.id ? pdata : null);
    setProfile(prof.status === "fulfilled" ? (prof.value.data || {}) : {});
    setLogs(lg.status === "fulfilled" ? (lg.value.data || []) : []);
    setReviews(rev.status === "fulfilled" ? (rev.value.data || []) : []);
  };

  const validate = async () => {
    await api.post(`/programs/${program.id}/validate`);
    setProgram({ ...program, status: "active" });
    toast.success("Programme validé");
    loadAthletes();
  };

  const regenerate = async (instructions) => {
    setGenerating(true);
    try {
      await api.post("/programs/generate", { athlete_id: selected.id, instructions: instructions || undefined });
      const poll = async () => {
        try {
          const list = (await api.get("/coach/athletes")).data;
          const pid = list.find((x) => x.id === selected.id)?.program_id;
          if (!pid) { setTimeout(poll, 3000); return; }
          const p = (await api.get(`/programs/${pid}`)).data;
          if (p && p.status === "generating") { setTimeout(poll, 3000); return; }
          if (p && p.status === "error") { toast.error("Échec de génération IA."); setGenerating(false); return; }
          if (p && p.id) setProgram(p);
          toast.success("Nouveau programme généré");
          setGenerating(false);
          loadAthletes();
        } catch { setTimeout(poll, 3000); }
      };
      setTimeout(poll, 3000);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
      setGenerating(false);
    }
  };

  const saveSession = async (updated) => {
    const weeks = program.weeks.map((w) => ({ ...w, sessions: w.sessions.map((s) => (s.id === updated.id ? updated : s)) }));
    const { data } = await api.put(`/programs/${program.id}`, { weeks });
    setProgram(data);
    toast.success("Séance mise à jour");
  };

  const clearIssue = async (sessionId) => {
    const weeks = program.weeks.map((w) => ({
      ...w,
      sessions: w.sessions.map((s) => {
        if (s.id !== sessionId) return s;
        const { issue, ...rest } = s;
        return rest;
      }),
    }));
    const { data } = await api.put(`/programs/${program.id}`, { weeks });
    setProgram(data);
    toast.success("Empêchement traité");
    loadAthletes();
  };

  const refreshSelected = async () => {
    const { data } = await api.get("/coach/athletes");
    setAthletes(data || []);
    if (selected) {
      const fresh = (data || []).find((x) => x.id === selected.id);
      if (fresh) setSelected(fresh);
    }
  };

  const applyObjective = async (reqId) => {
    try {
      await api.post(`/coach/requests/${reqId}/apply`);
      toast.success("Nouvel objectif appliqué. Tu peux régénérer le programme.");
      await refreshSelected();
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || err.message); }
  };

  const dismissObjective = async (reqId) => {
    try {
      await api.post(`/coach/requests/${reqId}/dismiss`);
      toast.success("Demande ignorée.");
      await refreshSelected();
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || err.message); }
  };

  const openRegenSession = (s) => {
    const issue = s.issue;
    setRegenSessText(issue ? `L'athlète a signalé : ${ISSUE_LABEL[issue.reason] || issue.reason}${issue.note ? ` (${issue.note})` : ""}. Adapte la séance en conséquence.` : "");
    setRegenSession(s);
  };

  const regenerateSession = async () => {
    setRegenSessLoading(true);
    try {
      const { data } = await api.post(`/programs/${program.id}/sessions/${regenSession.id}/regenerate`, { instructions: regenSessText || undefined });
      setProgram(data);
      toast.success("Séance régénérée par l'IA");
      setRegenSession(null);
      loadAthletes();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setRegenSessLoading(false);
    }
  };

  const wbData = logs.filter((l) => l.wall_balls_unbroken).map((l, i) => ({ label: `S${i + 1}`, value: l.wall_balls_unbroken }));
  const paceData = logs.filter((l) => paceToSec(l.compromised_pace)).map((l, i) => ({ label: `S${i + 1}`, value: paceToSec(l.compromised_pace) }));

  return (
    <div className="min-h-screen bg-background pb-16">
      <header className="flex items-center justify-between px-5 md:px-8 py-4 border-b border-border sticky top-0 bg-background/95 backdrop-blur z-20">
        <div className="flex items-center gap-2">
          <Flame className="text-primary" size={22} strokeWidth={2.5} />
          <span className="font-display text-xl">ROX<span className="text-primary">LAB</span></span>
          <Badge className="ml-2 bg-primary/15 text-primary border-0">Coach</Badge>
        </div>
        <Button data-testid="coach-logout-btn" variant="ghost" size="icon" onClick={() => { logout(); nav("/"); }}><LogOut size={18} /></Button>
      </header>

      <div className="max-w-4xl mx-auto px-5 pt-6">
        {!selected ? (
          showStats ? (
            <>
              <button data-testid="stats-back-btn" onClick={() => setShowStats(false)} className="flex items-center gap-1 text-sm text-muted-foreground mb-4 hover:text-foreground">
                <ChevronLeft size={16} /> Retour aux athlètes
              </button>
              <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
                <h1 className="font-display text-3xl flex items-center gap-2"><BarChart3 size={26} /> Statistiques bêta</h1>
                <Button data-testid="stats-export-btn" onClick={exportCsv} variant="outline" size="sm" className="border-border bg-transparent hover:bg-secondary"><Download size={14} className="mr-1.5" /> Exporter CSV</Button>
              </div>
              {!stats ? (
                <p className="text-muted-foreground text-center py-10">Chargement…</p>
              ) : (
                <div className="space-y-5" data-testid="stats-view">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-card border border-border rounded-lg p-4"><p className="text-[10px] uppercase tracking-widest text-muted-foreground">Athlètes</p><p className="font-display text-2xl">{stats.athlete_count}</p></div>
                    <div className="bg-card border border-border rounded-lg p-4"><p className="text-[10px] uppercase tracking-widest text-muted-foreground">Onboarding fini</p><p className="font-display text-2xl">{stats.onboarding.completed}<span className="text-sm text-muted-foreground">/{stats.onboarding.started}</span></p></div>
                    <div className="bg-card border border-border rounded-lg p-4"><p className="text-[10px] uppercase tracking-widest text-muted-foreground">Taux logging</p><p className="font-display text-2xl">{stats.logging.rate != null ? `${Math.round(stats.logging.rate * 100)}%` : "—"}</p><p className="text-[10px] text-muted-foreground">{stats.logging.done}/{stats.logging.planned} séances</p></div>
                    <div className="bg-card border border-border rounded-lg p-4"><p className="text-[10px] uppercase tracking-widest text-muted-foreground">Strava connecté</p><p className="font-display text-2xl">{stats.strava_connected}</p></div>
                  </div>

                  <div className="bg-card border border-border rounded-lg p-5">
                    <h3 className="text-sm uppercase tracking-widest text-muted-foreground mb-3">Funnel onboarding</h3>
                    <div className="space-y-2">
                      {stats.onboarding.funnel.map((f) => {
                        const pct = stats.onboarding.started ? Math.round((f.reached / stats.onboarding.started) * 100) : 0;
                        return (
                          <div key={f.step} className="flex items-center gap-3">
                            <span className="text-xs text-muted-foreground w-16 shrink-0">Étape {f.step}</span>
                            <div className="flex-1 h-4 bg-secondary rounded overflow-hidden"><div className="h-full bg-primary" style={{ width: `${pct}%` }} /></div>
                            <span className="text-xs tabular-nums w-16 text-right">{f.reached} ({pct}%)</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="bg-card border border-border rounded-lg p-5">
                      <h3 className="text-sm uppercase tracking-widest text-muted-foreground mb-3">Visites par onglet</h3>
                      <div className="space-y-1.5 text-sm">
                        <div className="flex justify-between"><span>Programme</span><span className="tabular-nums font-semibold">{stats.tab_totals.program}</span></div>
                        <div className="flex justify-between"><span>Progression</span><span className="tabular-nums font-semibold">{stats.tab_totals.progress}</span></div>
                        <div className="flex justify-between"><span>Nutrition</span><span className="tabular-nums font-semibold">{stats.tab_totals.nutrition}</span></div>
                      </div>
                    </div>
                    <div className="bg-card border border-border rounded-lg p-5">
                      <h3 className="text-sm uppercase tracking-widest text-muted-foreground mb-3">Outils</h3>
                      <div className="space-y-1.5 text-sm">
                        <div className="flex justify-between"><span>Calculateur d'allure</span><span className="tabular-nums font-semibold">{stats.pace_calc_uses} ouvertures</span></div>
                        <div className="flex justify-between"><span>Strava connecté</span><span className="tabular-nums font-semibold">{stats.strava_connected} athlète(s)</span></div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-card border border-border rounded-lg p-5 overflow-x-auto">
                    <h3 className="text-sm uppercase tracking-widest text-muted-foreground mb-3">Détail par athlète</h3>
                    <table className="w-full text-sm min-w-[560px]">
                      <thead><tr className="text-left text-muted-foreground text-xs uppercase tracking-widest">
                        <th className="py-2 pr-3">Athlète</th><th className="py-2 px-2">Onb.</th><th className="py-2 px-2">Prog</th><th className="py-2 px-2">Prog.</th><th className="py-2 px-2">Nutr</th><th className="py-2 px-2">Logging</th><th className="py-2 px-2">Strava</th><th className="py-2 px-2">Allure</th>
                      </tr></thead>
                      <tbody>
                        {stats.per_athlete.map((r) => (
                          <tr key={r.id} className="border-t border-border">
                            <td className="py-2 pr-3"><p className="font-medium">{r.name}</p><p className="text-[11px] text-muted-foreground">{r.email}</p></td>
                            <td className="py-2 px-2">{r.onboarding_completed ? "✅" : `${r.onboarding_max_step}/4`}</td>
                            <td className="py-2 px-2 tabular-nums">{r.tab_program}</td>
                            <td className="py-2 px-2 tabular-nums">{r.tab_progress}</td>
                            <td className="py-2 px-2 tabular-nums">{r.tab_nutrition}</td>
                            <td className="py-2 px-2 tabular-nums">{r.sessions_total ? `${r.sessions_done}/${r.sessions_total}` : "—"}</td>
                            <td className="py-2 px-2">{r.strava_connected ? "✅" : "—"}</td>
                            <td className="py-2 px-2 tabular-nums">{r.pace_calc_uses}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="bg-card border border-border rounded-lg p-5" data-testid="stats-feedback">
                    <h3 className="text-sm uppercase tracking-widest text-muted-foreground mb-3">Retours des athlètes ({(stats.feedback || []).length})</h3>
                    {(stats.feedback || []).length === 0 ? (
                      <p className="text-sm text-muted-foreground">Aucun retour pour l'instant.</p>
                    ) : (
                      <div className="space-y-2">
                        {stats.feedback.map((f) => (
                          <div key={f.id} className="border border-border rounded-md px-3 py-2.5 bg-secondary/40">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <p className="text-sm font-medium">{f.name}</p>
                              <p className="text-[11px] text-muted-foreground">{formatFrDate(f.ts)}</p>
                            </div>
                            <p className="text-sm text-foreground whitespace-pre-wrap">{f.message}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
          <>
            <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
              <h1 className="font-display text-3xl flex items-center gap-2"><Users size={26} /> Mes athlètes</h1>
              <Button data-testid="coach-stats-btn" onClick={loadStats} variant="outline" size="sm" className="border-border bg-transparent hover:bg-secondary"><BarChart3 size={14} className="mr-1.5" /> Statistiques</Button>
            </div>
            <p className="text-sm text-muted-foreground mb-6">{athletes.length} athlète(s) suivis</p>
            <div className="space-y-3">
              {athletes.length === 0 && <p className="text-muted-foreground text-center py-10">Aucun athlète inscrit pour l'instant.</p>}
              {athletes.map((a) => (
                <div key={a.id} data-testid={`athlete-card-${a.id}`} onClick={() => openAthlete(a)}
                  className="bg-card border border-border rounded-lg p-5 cursor-pointer hover:border-primary/50 transition-colors">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-lg">{a.name}</p>
                      <p className="text-xs text-muted-foreground">{a.email}</p>
                    </div>
                    <Badge className={a.program_status === "active" ? "bg-green-500/15 text-green-400 border-0" : a.program_status === "draft" ? "bg-amber-500/15 text-amber-400 border-0" : "bg-secondary text-muted-foreground border-0"}>
                      {a.program_status === "active" ? "Actif" : a.program_status === "draft" ? "À valider" : "Aucun"}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                    {a.target_time && <span>🎯 {a.target_time}</span>}
                    <span>{a.done_sessions}/{a.total_sessions} séances</span>
                  </div>
                  {a.alerts.map((al, i) => (
                    <div key={i} className="flex items-center gap-2 mt-2 text-sm text-primary"><AlertTriangle size={15} /> {al}</div>
                  ))}
                </div>
              ))}
            </div>
          </>
          )
        ) : (
          <>
            <button data-testid="coach-back-btn" onClick={() => { setSelected(null); setProgram(null); }} className="flex items-center gap-1 text-sm text-muted-foreground mb-4 hover:text-foreground">
              <ChevronLeft size={16} /> Retour à la liste
            </button>
            <div className="bg-card border border-border rounded-lg p-5 mb-5">
              <h1 className="font-display text-3xl">{selected.name}</h1>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-sm">
                <div><p className="text-xs uppercase tracking-widest text-muted-foreground">Objectif</p><p className="text-primary font-semibold">{profile.target_time || "—"}</p></div>
                <div><p className="text-xs uppercase tracking-widest text-muted-foreground">Niveau</p><p className="capitalize">{profile.level || "—"}</p></div>
                <div><p className="text-xs uppercase tracking-widest text-muted-foreground">Course</p><p>{formatFrDate(profile.race_date)}</p></div>
                <div><p className="text-xs uppercase tracking-widest text-muted-foreground">Stations faibles</p><p className="capitalize">{(profile.weak_stations || []).join(", ") || "—"}</p></div>
              </div>
            </div>

            {selected.objective_request && (
              <div data-testid="coach-objective-request" className="bg-primary/10 border border-primary/40 rounded-lg p-4 mb-5">
                <div className="flex items-center gap-2 text-primary font-semibold mb-2"><Target size={17} /> Demande de changement d'objectif</div>
                <div className="grid grid-cols-2 gap-3 text-sm mb-1">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">Actuel</p>
                    <p className="line-through text-muted-foreground">{selected.objective_request.old_target_time || "—"}{selected.objective_request.old_race_date ? ` · ${selected.objective_request.old_race_date}` : ""}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">Demandé</p>
                    <p className="text-foreground font-bold text-base">{selected.objective_request.new_target_time || "—"}{selected.objective_request.new_race_date ? ` · ${selected.objective_request.new_race_date}` : ""}</p>
                  </div>
                </div>
                {selected.objective_request.note && <p className="text-xs text-muted-foreground italic mb-3">« {selected.objective_request.note} »</p>}
                <div className="flex gap-2 flex-wrap mt-2">
                  <Button data-testid="coach-obj-apply-btn" size="sm" onClick={() => applyObjective(selected.objective_request.id)} className="bg-primary hover:bg-primary/90 text-white">Appliquer l'objectif</Button>
                  <Button data-testid="coach-obj-dismiss-btn" size="sm" variant="outline" onClick={() => dismissObjective(selected.objective_request.id)} className="border-border bg-transparent hover:bg-secondary">Ignorer</Button>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">Appliquer met à jour l'objectif du profil. Régénère ensuite le programme quand tu le souhaites.</p>
              </div>
            )}

            {profile.about_me && (
              <div data-testid="coach-about-me" className="bg-card border border-border rounded-lg p-4 mb-5">
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Contexte de l'athlète</p>
                <p className="text-sm text-foreground whitespace-pre-wrap">{profile.about_me}</p>
              </div>
            )}

            {reviews.length > 0 && (
              <div data-testid="coach-weekly-reviews" className="bg-card border border-border rounded-lg p-4 mb-5">
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Bilans hebdomadaires (ressenti athlète)</p>
                <div className="space-y-2">
                  {[...reviews].reverse().map((r) => {
                    const lbl = { too_easy: "Trop facile", just_right: "Juste bien", too_hard: "Trop dur" }[r.difficulty] || r.difficulty;
                    const color = r.difficulty === "too_hard" ? "bg-primary/20 text-primary" : r.difficulty === "too_easy" ? "bg-blue-500/20 text-blue-300" : "bg-green-500/20 text-green-300";
                    return (
                      <div key={r.week_number} className="border border-border rounded-md px-3 py-2.5 bg-secondary/40">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-semibold">Semaine {r.week_number}</span>
                          <span className={`text-xs font-bold px-2 py-1 rounded ${color}`}>{lbl}</span>
                        </div>
                        {r.stations && r.stations.length > 0 && (
                          <p className="text-xs text-muted-foreground mt-1.5">
                            {r.stations.map((s) => `${s.name} : ${{ too_easy: "trop facile", too_hard: "trop dur" }[s.difficulty] || s.difficulty}`).join(" · ")}
                          </p>
                        )}
                        {r.note && <p className="text-sm text-foreground mt-1.5 italic">« {r.note} »</p>}
                      </div>
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground mt-3">Ces ressentis sont automatiquement transmis à l'IA lors de la régénération pour calibrer charge et volume.</p>
              </div>
            )}

            {selected.alerts && selected.alerts.length > 0 && (
              <div data-testid="coach-alerts" className="bg-primary/10 border border-primary/40 rounded-lg p-4 mb-5 space-y-1.5">
                {selected.alerts.map((al, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-primary font-medium"><AlertTriangle size={16} /> {al}</div>
                ))}
              </div>
            )}

            {(wbData.length > 0 || paceData.length > 0) && (
              <div className="grid md:grid-cols-2 gap-3 mb-5">
                <MiniChart title="Wall balls unbroken" data={wbData} color="#FF3B30" />
                <MiniChart title="Pace compromised (s)" data={paceData} color="#FF4500" invert />
              </div>
            )}

            {logs.length > 0 && (
              <div data-testid="coach-log-history" className="bg-card border border-border rounded-lg p-4 mb-5">
                <h4 className="text-sm uppercase tracking-widest text-muted-foreground mb-3">Séances loggées ({logs.length})</h4>
                <div className="space-y-2">
                  {[...logs].reverse().slice(0, 12).map((l, i) => (
                    <div key={l.id || i} className="flex items-center justify-between border border-border rounded-md px-3 py-2.5 bg-secondary/40">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{l.session_title || "Séance"}</p>
                        <p className="text-xs text-muted-foreground">{formatFrDate(l.logged_at)}</p>
                      </div>
                      {l.rpe ? (
                        <span className={`px-2 py-1 rounded text-xs font-bold shrink-0 ${l.rpe >= 9 ? "bg-primary/20 text-primary" : "bg-secondary text-foreground"}`}>RPE {l.rpe}</span>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!program ? (
              <div className="bg-card border border-border rounded-lg p-8 text-center">
                <p className="text-muted-foreground mb-4">{selected.has_profile ? "Aucun programme. Génère-le pour cet athlète." : "L'athlète doit compléter son profil."}</p>
                {selected.has_profile && (
                  <Button data-testid="coach-generate-btn" onClick={regenerate} disabled={generating} className="bg-primary hover:bg-primary/90 text-white rounded-md h-12 px-6 font-semibold">
                    {generating ? <><RefreshCw size={16} className="animate-spin mr-2" /> Génération…</> : "Générer le programme"}
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div>
                    <h2 className="font-display text-2xl">{program.name}</h2>
                    <Badge className={`mt-1 ${program.status === "active" ? "bg-green-500/15 text-green-400" : "bg-amber-500/15 text-amber-400"} border-0`}>
                      {program.status === "active" ? "Validé" : "Brouillon"}
                    </Badge>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button data-testid="coach-regen-btn" variant="outline" size="sm" onClick={() => regenerate()} disabled={generating} className="border-border bg-transparent hover:bg-secondary">
                      <RefreshCw size={14} className={generating ? "animate-spin" : ""} /> Régénérer
                    </Button>
                    <Button data-testid="coach-regen-instr-btn" variant="outline" size="sm" onClick={() => { setRegenText(""); setRegenOpen(true); }} disabled={generating} className="border-border bg-transparent hover:bg-secondary">
                      <Pencil size={14} className="mr-1" /> Régénérer avec instructions
                    </Button>
                    {program.status !== "active" && (
                      <Button data-testid="coach-validate-btn" size="sm" onClick={validate} className="bg-green-600 hover:bg-green-700 text-white">
                        <CheckCircle2 size={14} className="mr-1" /> Valider le programme
                      </Button>
                    )}
                  </div>
                </div>
                <Accordion type="single" collapsible defaultValue="w-0" className="space-y-2">
                  {program.weeks.map((w, wi) => (
                    <AccordionItem key={wi} value={`w-${wi}`} className="bg-card border border-border rounded-lg px-4">
                      <AccordionTrigger data-testid={`coach-week-${wi}`} className="hover:no-underline py-4">
                        <span className="font-display text-lg">Semaine {w.week_number}</span>
                        <span className="ml-2 text-xs uppercase tracking-widest text-primary">{w.phase}</span>
                      </AccordionTrigger>
                      <AccordionContent className="pb-4 space-y-2">
                        {w.sessions.map((s) => (
                          <div key={s.id} className={`border rounded-md p-4 ${s.issue ? "border-amber-500/50 bg-amber-500/5" : "border-border bg-secondary/40"}`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs text-muted-foreground">{TYPE_LABEL[s.type]} · {s.day} · {s.time_slot}</span>
                              <div className="flex items-center gap-2 shrink-0">
                                <button data-testid={`coach-regen-session-${s.id}`} onClick={() => openRegenSession(s)} title="Régénérer cette séance" className="text-muted-foreground hover:text-primary"><RefreshCw size={15} /></button>
                                <button data-testid={`coach-edit-${s.id}`} onClick={() => setEditSession(s)} title="Éditer" className="text-muted-foreground hover:text-primary"><Pencil size={16} /></button>
                              </div>
                            </div>
                            <p className="font-semibold">{s.title}</p>
                            {s.main && <ul className="list-disc list-inside text-sm text-muted-foreground mt-1">{s.main.map((m, mi) => <li key={mi}>{m}</li>)}</ul>}
                            {s.finisher && <p className="text-sm text-primary mt-1">Finisher: {s.finisher}</p>}
                            {s.completed && s.log && <p className="text-xs text-green-400 mt-2">✓ Loggée — RPE {s.log.rpe ?? "—"}</p>}
                            {s.issue && (
                              <div data-testid={`coach-issue-${s.id}`} className="mt-2 flex items-start justify-between gap-3 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2">
                                <div className="flex items-start gap-2 text-xs text-amber-400 min-w-0">
                                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                                  <span><b>Empêchement : {ISSUE_LABEL[s.issue.reason] || s.issue.reason}</b>{s.issue.note ? ` — ${s.issue.note}` : ""}</span>
                                </div>
                                <button data-testid={`coach-clear-issue-${s.id}`} onClick={() => clearIssue(s.id)} className="text-xs text-muted-foreground hover:text-foreground underline shrink-0">Traité</button>
                              </div>
                            )}
                          </div>
                        ))}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </>
            )}
          </>
        )}
      </div>
      <EditSessionDialog open={!!editSession} onClose={() => setEditSession(null)} session={editSession} onSave={saveSession} />
      <Dialog open={regenOpen} onOpenChange={setRegenOpen}>
        <DialogContent className="bg-card border-border w-[calc(100%-1.5rem)] max-w-lg max-h-[90vh] overflow-y-auto p-5 sm:p-6">
          <DialogHeader><DialogTitle className="font-display text-xl">Régénérer avec instructions</DialogTitle>
            <DialogDescription className="text-muted-foreground">Donne des consignes à l'IA (ex : « plus de volume course », « réduire la charge en semaine 1 », « ajouter du travail sled »). Un nouveau programme sera généré.</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="regen-instructions-input" value={regenText} onChange={(e) => setRegenText(e.target.value)} placeholder="Ex : Plus de volume course, réduire la charge semaine 1, focus wall balls…" className="bg-secondary border-border min-h-[110px] text-base" />
          <Button data-testid="regen-submit-btn" onClick={() => { setRegenOpen(false); regenerate(regenText); }} disabled={!regenText.trim()} className="w-full h-12 bg-primary hover:bg-primary/90 text-white rounded-md font-semibold">
            Régénérer le programme
          </Button>
        </DialogContent>
      </Dialog>
      <Dialog open={!!regenSession} onOpenChange={(o) => !o && !regenSessLoading && setRegenSession(null)}>
        <DialogContent className="bg-card border-border w-[calc(100%-1.5rem)] max-w-lg max-h-[90vh] overflow-y-auto p-5 sm:p-6">
          <DialogHeader><DialogTitle className="font-display text-xl">Régénérer cette séance</DialogTitle>
            <DialogDescription className="text-muted-foreground">Seule cette séance est régénérée par l'IA (jour, créneau et type conservés). Le reste du programme reste inchangé. Idéal après un empêchement signalé.</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="regen-session-input" value={regenSessText} onChange={(e) => setRegenSessText(e.target.value)} placeholder="Consignes optionnelles : alléger, éviter le genou, plus de course, focus wall balls…" className="bg-secondary border-border min-h-[110px] text-base" />
          <Button data-testid="regen-session-submit-btn" onClick={regenerateSession} disabled={regenSessLoading} className="w-full h-12 bg-primary hover:bg-primary/90 text-white rounded-md font-semibold">
            {regenSessLoading ? <><RefreshCw size={16} className="animate-spin mr-2" /> Génération IA…</> : "Régénérer la séance"}
          </Button>
        </DialogContent>
      </Dialog>

    </div>
  );
}
